import { FragmentConfiguration } from '@/config';
import useConfig from '@empiriecom/module-components/hooks/useConfig';
import { ErrorDetails, UseFetchParams } from '@empiriecom/module-components/hooks/useFetch';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { useProduct } from '../../ProductContext';
import { CalculationResult, InstallmentRequest } from '../types';

export interface InstallmentCalculatorContextProps {
  error: ErrorDetails;
  installmentPrice: number;
  installmentRequestSource: string;
  isLoading: boolean;
  isPaymentPause: boolean;
  isPriceEditable: boolean;
  numberOfInstallments: number;
  response: CalculationResult;
  setInstallmentPrice: (value: number) => void;
  setInstallmentRequestSource: (src: string) => void;
  setIsPaymentPause: (flag: boolean) => void;
  setIsPriceEditable: (flag: boolean) => void;
  setNumberOfInstallments: (value: number) => void;
}

export const InstallmentCalculatorContext = createContext<InstallmentCalculatorContextProps>(null);

export const useInstallmentCalculatorContext = (): InstallmentCalculatorContextProps =>
  useContext(InstallmentCalculatorContext);

function handleResponse<SuccessResponse>(
  response: Response,
): Promise<SuccessResponse | ErrorDetails> {
  if (response.ok) {
    return response.json() as Promise<SuccessResponse>;
  }
  // eslint-disable-next-line
  return Promise.reject({
    description: response.statusText,
    statusCode: response.status,
  });
}

async function getResource<SuccessResponse>({
  url,
  fetchConfig,
  body,
}: UseFetchParams): Promise<SuccessResponse | ErrorDetails> {
  if (url) {
    const response = await fetch(url, {
      ...fetchConfig,
      body: JSON.stringify(body),
    });
    return handleResponse(response);
  }
  throw new Error('URL undefined');
}

export const InstallmentCalculatorContextProvider = ({ children, ...props }): JSX.Element => {
  const {
    product: { price },
  } = useProduct();

  const [installments, setInstallments] = useState<number>();
  const [paymentPause, setPaymentPause] = useState<boolean>(false);
  const [priceValue, setPriceValue] = useState<number>();
  const [priceEditable, setPriceEditable] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<ErrorDetails>();
  const [response, setResponse] = useState<CalculationResult>();
  const [requestSource, setRequestSource] = useState<string>('');

  useEffect(() => setPriceValue(price?.value), [price]);

  const {
    installmentCalculator: { apiInstallmentCalculator },
  } = useConfig<FragmentConfiguration>();

  const fetchConfig = {
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    method: 'POST',
  };

  let body: InstallmentRequest = {
    basevalue: priceValue / 100,
    source: requestSource,
    paymentpause: paymentPause,
  };
  if (installments > 0) {
    body = { ...body, selectedNumberOfInstallments: installments };
  }

  useEffect(() => {
    if (typeof window !== 'undefined') {
      setIsLoading(true);
      getResource<CalculationResult>({
        body,
        fetchConfig,
        url: apiInstallmentCalculator,
      })
        .then((data) => {
          setResponse(data as CalculationResult);
          setIsLoading(false);
          setError(null);
        })
        .catch((err) => {
          setError(err);
          setIsLoading(false);
          setResponse(null);
        });
    }
  }, [priceValue, requestSource, installments, paymentPause]);

  return (
    <InstallmentCalculatorContext.Provider
      value={{
        error,
        installmentPrice: priceValue,
        installmentRequestSource: requestSource,
        isLoading,
        isPaymentPause: paymentPause,
        isPriceEditable: priceEditable,
        numberOfInstallments: installments,
        response,
        setInstallmentPrice: (value: number) => setPriceValue(value),
        setInstallmentRequestSource: (src: string) => setRequestSource(src),
        setIsPaymentPause: (flag: boolean) => setPaymentPause(flag),
        setIsPriceEditable: (isEditable: boolean) => setPriceEditable(isEditable),
        setNumberOfInstallments: (value: number) => setInstallments(value),
      }}
      // props need to be passed in this way
      // eslint-disable-next-line
      {...props}
    >
      {children}
    </InstallmentCalculatorContext.Provider>
  );
};

export default InstallmentCalculatorContextProvider;
